import { differenceInYears } from "date-fns";
import {
  FieldPath,
  FieldPathValue,
  FieldValues,
  RegisterOptions,
  Validate,
} from "react-hook-form";
import i18n from "../i18n/i18nConfig";
import { inferDateFormat } from "./timeFormats";

const { t } = i18n;

type Rules = Omit<
  RegisterOptions,
  | "valueAsNumber"
  | "valueAsDate"
  | "setValueAs"
  | "disabled"
  | "validate"
  | "deps"
> & {
  // require validate functions to have key names for easier merging
  validate?: Record<
    string,
    Validate<FieldPathValue<FieldValues, FieldPath<FieldValues>>, FieldValues>
  >;
};

export function mergeRules(...rules: Rules[]): Rules {
  return rules.reduce(
    (acc, rule) => ({
      ...acc,
      ...rule,
      validate: { ...acc.validate, ...rule.validate },
    }),
    {}
  );
}

/**
 * isRequired marks a field as required. This should result in creating an
 * aria-required attribute on the input field in addition to adding validation
 * logic.
 *
 * @param message should mention the field name, like "Email is required"
 */
export function isRequired(message: string): Rules {
  return {
    required: {
      value: true,
      message,
    },
  };
}

/**
 * Not explicitly necessary, but it is a good idea to have a function that clarifies that.
 */
export function isNotRequired(): Rules {
  return {
    required: false,
  };
}

/**
 * isRequiredBoolean is used for checkboxes that are required to be selected.
 *
 * @param message should mention the field name, like "Please accept the terms of use."
 */
export function isRequiredBoolean(message: string): Rules {
  return {
    validate: {
      requiredTrue: (value) => Boolean(value) || message,
    },
  };
}

/**
 * Why? Should this be standard?
 */
export function isRequiredString(message: string): Rules {
  return {
    required: {
      value: true,
      message,
    },
    validate: {
      extraRequired: (v) => Boolean(v && v.trim()) || message,
    },
  };
}

/**
 * hasMinLength is shorthand for specifying only a minimum length requirement.
 *
 * @param message should mention the field name, like "Password should be at least 6 characters long."
 */
export function hasMinLength(minLength: number, message: string): Rules {
  return {
    minLength: {
      value: minLength,
      message,
    },
  };
}

/**
 * hasMinValue is shorthand for specifying only a minimum value requirement.
 * This is useful for number fields.
 * @param message should mention the field name, like "Age must be at least 13."
 */
export function hasMinValue(minValue: number): Rules {
  return {
    min: {
      value: minValue,
      message: t("Minimum value is {{number}}", { number: minValue }),
    },
  };
}

/**
 * hasMaxValue is shorthand for specifying only a maximum value requirement.
 * This is useful for number fields.
 * @param message should mention the field name, like "Age must be at most 129."
 */
export function hasMaxValue(maxValue: number): Rules {
  return {
    max: {
      value: maxValue,
      message: t("Maximum value is {{number}}", { number: maxValue }),
    },
  };
}

/**
 * hasMaxLength is shorthand for specifying only a maximum length requirement.
 *
 * @param message should mention the field name, like "Phone number should be at most 15 characters long."
 */
export function hasMaxLength(maxLength: number, message: string): Rules {
  return {
    maxLength: {
      value: maxLength,
      message,
    },
  };
}

/**
 * testMalanEmail verifies that an email address matches the rules implemented
 * by Malan.
 * This matches the validation used in Malan for email addresses, but prevents trailing dots.
 *
 * https://github.com/FreedomBen/malan/blob/9e3be0821ef0771ec9829f50146f36ae28296b09/lib/malan/accounts/user.ex#L278-L288
 */
export function testMalanEmail(value: string): boolean {
  const email = value.toLowerCase();
  if (email.length < 6 || email.length > 150) return false;
  if (/@.*@/.test(email)) return false;
  if (/^\|/.test(email)) return false;
  return /^[!#$%&'*+-/=?^_`{|}~A-Za-z0-9]{0,63}[A-Za-z0-9_!]@[.-A-Za-z0-9]{1,63}\.[A-Za-z]{2,25}$/.test(
    email
  );
}

/**
 * isEmail specifies that the field must meet email requirements.
 */
export function isEmail(): Rules {
  return {
    validate: {
      email: (value) =>
        testMalanEmail(value) ||
        // NOTE: https://github.com/typescript-eslint/typescript-eslint/issues/11242
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        (t("Please enter a valid email address.") as string),
    },
  };
}

/**
 * isPassword specifies that the field must meet password requirements.
 */
export function isPassword(length: number = 8): Rules {
  const message = t("Password should be at least {{length}} characters long.", {
    length,
  });
  return mergeRules(isRequired(message), hasMinLength(length, message));
}

/**
 * isPasswordConfirmation specifies that the field must match the value of another field.
 */
export function isPasswordConfirmation(passwordFieldName: string): Rules {
  return {
    validate: {
      passwordConfirmation: (value, data) => {
        const password = data[passwordFieldName];
        // NOTE: https://github.com/typescript-eslint/typescript-eslint/issues/11242
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return value === password || (t("Passwords must match.") as string);
      },
    },
  };
}

export const MAX_PHONE_NUMBER_LENGTH = 20;
const ALLOWED_PHONE_NUMBER_CHARS = /^(\d|\(|\)|-|\+|\s)+$/;
/**
 * isPhoneNumber specifies that the field must meet phone number requirements.
 */
export function isPhoneNumber(): Rules {
  return mergeRules(
    hasMaxLength(
      MAX_PHONE_NUMBER_LENGTH,
      t("Phone number cannot be more than {{number}} digits.", {
        number: MAX_PHONE_NUMBER_LENGTH,
      })
    ),
    {
      pattern: {
        value: ALLOWED_PHONE_NUMBER_CHARS,
        message: t(
          "Phone number cannot contain letters or special characters, except for +, (, ), and -."
        ),
      },
    }
  );
}

export const MAX_USER_AGE = 129;
export const MIN_USER_AGE = 13;

/**
 * isDateOfBirth specifies that the field must meet date of birth requirements.
 */
export function isDateOfBirth(): Rules {
  return mergeRules(isRequired(t("Please enter your date of birth.")), {
    validate: {
      dateOfBirth: (value?: Date) => {
        const invalidDate = t("Please enter a valid date.");
        // Invalid data (or value = undefined - though I believe the undefined case
        // will be handled by required)
        if (!value || value?.toString() === "Invalid Date") return invalidDate;

        if (value.getTime() > new Date().getTime())
          // NOTE: https://github.com/typescript-eslint/typescript-eslint/issues/11242
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          return t("Please enter a date in the past.") as string;

        // Older than oldest age ever recorded
        const age = differenceInYears(new Date(), value);
        if (age > MAX_USER_AGE || age <= 0) return invalidDate;

        // Younger than allowed
        if (age < MIN_USER_AGE)
          // NOTE: https://github.com/typescript-eslint/typescript-eslint/issues/11242
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          return t(
            "You must be at least {{minUserAge}} years old to have an Ameelio Connect account.",
            { minUserAge: MIN_USER_AGE }
          ) as string;
        // Checks passed
        return true;
      },
    },
  });
}

export function isValidDateOfBirth(): Rules {
  return {
    validate: {
      validDateOfBirth: (value: string) => {
        const isInvalidDate = Number.isNaN(new Date(value).getTime());
        if (isInvalidDate) {
          // NOTE: https://github.com/typescript-eslint/typescript-eslint/issues/11242
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          return t("Please enter a date in the format {{format}}.", {
            format: inferDateFormat().toLowerCase().replace(/-/gi, "/"),
          }) as string;
        }
        return true;
      },
    },
  };
}

const ALLOWED_CHARACTERS =
  /^[\x20-\x7E|àèòùáéíóúñÑÁÉÍÓÚ¿¡ôêû|(?<=\w)['"’”\]]*$/m;
export function isStandardCharacters(): Rules {
  return {
    pattern: {
      value: ALLOWED_CHARACTERS,
      message: t(
        "Due to facility restrictions, emoji and special characters are not allowed."
      ),
    },
  };
}

const URL_PATTERN = /^(https:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/; // NB https is optional in the pattern
export function isValidUrl(): Rules {
  return {
    pattern: {
      value: URL_PATTERN,
      message: t("Please enter a valid url."),
    },
  };
}

export function isValidUUID(): Rules {
  return {
    pattern: {
      value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
      message: t("Please enter a valid UUID."),
    },
  };
}

export function isValidAmeelioEmail(): Rules {
  return mergeRules(isEmail(), {
    pattern: {
      value: /^[a-zA-Z0-9._%+-]+@ameelio\.org$/,
      message: t("Please enter a valid Ameelio email."),
    },
  });
}
